'use client';

import React from 'react';
import SharedLayout from '../components/SharedLayout';
import AboutCompany from './components/AboutCompany';
import Link from 'next/link';
import { MotionDiv } from '@/components/motion';
import { HelpCircle, FileText, ArrowRight } from 'lucide-react';

export default function CompanyPage() {
  return (
    <SharedLayout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-blue-50/30 to-purple-50/20 py-20 md:py-32">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10"></div>
        <div className="container mx-auto px-4">
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-block mb-4 px-4 py-2 bg-brand-primary/10 rounded-full">
              <span className="text-brand-primary font-semibold text-sm">Desde 1982</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Sobre <span className="text-brand-primary">Nosotros</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Más de 40 años de experiencia en la importación, distribución y comercialización de productos químicos de la más alta calidad
            </p>
          </MotionDiv>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <AboutCompany />
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/20">
        <div className="container mx-auto px-4">
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recursos Útiles
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Encuentre información detallada sobre nuestros servicios y políticas
            </p>
          </MotionDiv>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            <MotionDiv
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Link href="/empresa/faq" className="group block h-full">
                <div className="h-full bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-brand-primary/50 flex flex-col">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <HelpCircle className="h-7 w-7 text-white" />
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-brand-primary group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-brand-primary transition-colors duration-300">
                    Preguntas Frecuentes
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6 flex-grow">
                    Encuentre respuestas a las preguntas más comunes sobre nuestros productos, servicios y procesos de trabajo.
                  </p>
                  <div className="flex items-center text-brand-primary font-semibold mt-auto">
                    <span>Explorar FAQ</span>
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </Link>
            </MotionDiv>

            <MotionDiv
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Link href="/politicas" className="group block h-full">
                <div className="h-full bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-brand-primary/50 flex flex-col">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <FileText className="h-7 w-7 text-white" />
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-brand-primary group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-brand-primary transition-colors duration-300">
                    Nuestras Políticas
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6 flex-grow">
                    Conozca nuestras políticas de calidad, medio ambiente y compromiso social que guían nuestras operaciones.
                  </p>
                  <div className="flex items-center text-brand-primary font-semibold mt-auto">
                    <span>Ver políticas</span>
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </Link>
            </MotionDiv>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
} 